import React, { useState, useEffect } from 'react';
import { IoClose, IoAdd } from 'react-icons/io5';
import { BiTrash, BiPencil } from 'react-icons/bi';
import { getApiCaller, postApiCaller, deleteApiCaller } from '../../Libs/apiCaller';
import { decryptMessage as decrypt } from '../EncryptDecryptMessage/EncryptDecryptMessage';
import { toast } from 'react-toastify';

const ScheduledMessagesList = ({ isOpen, onClose, onNewMessage, onEdit, conversationId, secretKey }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            fetchScheduledMessages();
        }
    }, [isOpen]);

    const fetchScheduledMessages = async () => {
        try {
            setLoading(true);

            // Backend uses POST /messages/schedule/get with body
            const response = await postApiCaller('messages/schedule/get', {
                conversationId: conversationId
            });

            if (response?.status === 200 && response?.data?.data) {
                setMessages(response.data.data);
            }
        } catch (error) {
            console.error('[ScheduledList] Error fetching:', error);
            toast.error('Failed to load scheduled messages');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (messageId) => {
        try {
            const response = await deleteApiCaller(`messages/schedule/${messageId}`);

            if (response?.status === 200) {
                setMessages(messages.filter(m => m._id !== messageId));
                toast.success('Scheduled message deleted');
            } else {
                toast.error('Failed to delete message');
            }
        } catch (error) {
            console.error('[ScheduledList] Delete error:', error);
            toast.error('Error deleting message');
        }
    };

    const handleEdit = async (msg) => {
        try {
            // Decrypt the message
            const decryptedText = await decrypt(msg.cipherText, msg.iv, secretKey);

            // Delete the scheduled message
            await deleteApiCaller(`messages/schedule/${msg._id}`);

            // Call parent to populate input and open picker
            onEdit(decryptedText, new Date(msg.sentTime));

            // Remove from list
            setMessages(messages.filter(m => m._id !== msg._id));

            toast.info('Message loaded for editing');
        } catch (error) {
            console.error('[ScheduledList] Edit error:', error);
            toast.error('Error loading message');
        }
    };

    const formatDateTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const isSameDay = (d1, d2) => {
            return d1.getFullYear() === d2.getFullYear() &&
                d1.getMonth() === d2.getMonth() &&
                d1.getDate() === d2.getDate();
        };

        const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        if (isSameDay(date, now)) {
            return `Today ${timeStr}`;
        } else if (isSameDay(date, tomorrow)) {
            return `Tomorrow ${timeStr}`;
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }
    };

    const decryptMessage = async (cipherText, iv) => {
        try {
            return await decrypt(cipherText, iv, secretKey);
        } catch (error) {
            return '[Encrypted Message]';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed bottom-20 right-6 w-96 bg-white/90 backdrop-blur-2xl border border-white/40 shadow-2xl rounded-2xl p-4 z-50 animate-scaleIn max-h-[500px] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Pending Messages</h3>
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <IoClose className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            {/* Messages List */}
            <div className="flex-1 overflow-y-auto space-y-2 custom-scrollbar">
                {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No scheduled messages</p>
                        <p className="text-xs mt-1">Type a message and click the calendar to schedule one</p>
                    </div>
                ) : (
                    messages.map((msg) => (
                        <MessageCard
                            key={msg._id}
                            message={msg}
                            formatDateTime={formatDateTime}
                            decryptMessage={decryptMessage}
                            onDelete={() => handleDelete(msg._id)}
                            onEdit={() => handleEdit(msg)}
                        />
                    ))
                )}
            </div>

            <style jsx>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-scaleIn {
          animation: scaleIn 200ms ease-out;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 2px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
        </div>
    );
};

const MessageCard = ({ message, formatDateTime, decryptMessage, onDelete, onEdit }) => {
    const [decryptedText, setDecryptedText] = useState('');

    useEffect(() => {
        decryptMessage(message.cipherText, message.iv).then(setDecryptedText);
    }, [message]);

    const truncateText = (text, maxLength = 60) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="p-3 bg-white/50 rounded-xl border border-white/40 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-indigo-600 mb-1">
                        {formatDateTime(message.sentTime)}
                    </div>
                    <div className="text-sm text-gray-700 break-words">
                        {truncateText(decryptedText || 'Decrypting...')}
                    </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                    <button
                        onClick={onEdit}
                        className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Edit"
                    >
                        <BiPencil className="w-4 h-4 text-indigo-600" />
                    </button>
                    <button
                        onClick={onDelete}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                    >
                        <BiTrash className="w-4 h-4 text-red-600" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScheduledMessagesList;
